/**
 * Adds options for PIE charts.
 */
import * as transform from './transform';
import { renderInvalidatedColors } from './chart-util';
import * as format from '../data/format';


const TOP_LEFT = 0;
const TOP_RIGHT = 1;
const BOTTOM_RIGHT = 2;
const BOTTOM_LEFT = 3;

const PERC_MARGIN = 4;
const LABEL_MARGIN = 10;

export function preRender(data, options) {
  options = writeDataNames(options);
  let arrayofYValues = data.series[0].data;
  let sumofYValues = 0;
  arrayofYValues.forEach((data) =>{
    sumofYValues += data.y;
  });
  arrayofYValues.forEach((data) =>{
    data.y = data.y / sumofYValues;
    // data.y = format.decimals(data.y, options.axes.units, true, true);
    data.y = parseFloat(data.y * 100);
    let forceDecimals = Number(options.axes.units.forceDecimals);
    if (forceDecimals === 1) forceDecimals = 10;
    if (forceDecimals === 2) forceDecimals = 100;
    if (forceDecimals === 3) forceDecimals = 1000; 
    if (forceDecimals === 0) forceDecimals = 'round'
    if (forceDecimals) {
      data.y = forceDecimals !== 'round' ? Math.ceil(data.y * forceDecimals) / forceDecimals : data.y;
      if (data.y.toString().length > 4 && forceDecimals === 'round') {
        data.y = Math.round(data.y);
      }
    }
  })
  data.plotOptions.pie = {
    dataLabels: { enabled: false },
    enableMouseTracking: options.interactive,
    showInLegend: true,
    minSize: minSize(data, options),
    center: ['50%', '50%'],
    states: {
      hover: {
        enabled: false
      }
    }
  };
  return data;
}

// Writes the categories into the series[0] data so that they are available
// for data labels.
function writeDataNames(options) {
  options.series[0].data.forEach((item, index) => {
    if (options.type === 'pie' && options.optimizeDataOrder) {
      item.name = options.longCategories[item.originalIndex];
    } else {
      item.name = options.longCategories[index];
    }
  });
  return options;
}

// Set a minimum radius for the chart to render.
function minSize(data, options) {
  return Math.min(
    options.height - data.chart.spacing[0] - data.chart.spacing[2],
    options.width - data.chart.spacing[3] - data.chart.spacing[1]
  ) * 0.5;
}


/**
 * Draws the data labels using custom methods instead of Highcharts.
 * 1) Draw labels into corners of quadrants.
 * 2) Resize and move pie chart based on data labels locations.
 * 3) Draw connectors.
 */
export function postRender(highchart, options) {
  if (options.legend.enabled && options.size.indexOf('thumb') !== 0) {
    const g = drawLabelsGroup(highchart);
    const rect = getPieRect(highchart);
    if (rect) {
      const labels = drawLabels(highchart, options, g, rect);
      if (labels.length) {
        let sorted = sortLabels(labels);
        sorted = positionLabels(highchart, sorted, rect);
        const resizedRect = resizeChart(highchart, rect, sorted,
          labels[0].lineHeight);
        truncateLabels(highchart, labels, resizedRect);
        improveLabelPositions(highchart, labels, resizedRect);
        if (options.product === 'financialnews') {
          makeDonutChart(highchart, options);
        }
      }
    }
  }
}

// Creates a `g` element to contain the data labels, translated to the same
// position as the pie chart.
function drawLabelsGroup(highchart) {
  const gPieSelector = '.highcharts-series-group .highcharts-series';
  const gPie = highchart.container.querySelector(gPieSelector);
  const g = highchart.renderer.g().add();
  g.element.setAttribute('transform', gPie.getAttribute('transform'));
  g.element.setAttribute('class', 'pie-chart-data-labels');
  return g;
}


function drawLabels(highchart, options, g, rect) {
  let labels = [];
  let data = highchart.series[0].data;
  let smallSize = isSmall(options.size);
  let styleLabel = options.styles.get(smallSize ? 'small' : 'normal', {
    fontFamily: options.styles.font.bold,
    fontWeight: options.styles.weight.bold
  });
  let stylePerc = options.styles.get(smallSize ? 'small' : 'normal');
  let lineHeight = parseFloat(styleLabel.fontSize);
  let pi2 = Math.PI * 2;

  for (let i = 0; i < data.length; i++) {
    const slice = data[i];
    labels[i] = {};
    labels[i].slice = slice;
    labels[i].radians = (slice.angle + pi2) % pi2;
    labels[i].styleLabel = options.styles.get(smallSize ? 'small' : 'normal', {
      fontFamily: options.styles.font.bold,
      fontWeight: options.styles.weight.bold,
    });
    labels[i].stylePerc = options.styles.get(smallSize ? 'small' : 'normal', {
    });
    labels[i].g = highchart.renderer.g().add(g);

    // const labelName = getLabelName(slice, options);
    labels[i].labelName = highchart.renderer
      .text(slice.name, 0, 0)
      .css(labels[i].styleLabel)
      .add(labels[i].g);
    labels[i].labelPerc = highchart.renderer
      .text(slice.y + '%',
        labels[i].labelName.element.getBoundingClientRect().width +
        PERC_MARGIN, 0)
      .css(labels[i].stylePerc)
      .add(labels[i].g);
    labels[i].lineHeight = smallSize ? 14 :
      parseFloat(styleLabel.lineHeight);
    const isMW = options.product === 'marketwatch';
    const isFN = options.product === 'financialnews';
    const widthVal = isMW ? 12 : 9;
    const heightVal = isMW ? 12 : 13;
    if (!isFN) {
      highchart.renderer.rect().attr({
        width: widthVal,
        height: heightVal,
        fill: slice.color
      }).add(labels[i].g);
    } else {
      highchart.renderer.circle(5, -4, widthVal / 2).attr({
        fill: slice.color
      }).add(labels[i].g);
    }
    
    const xVal = isMW ? 15 : 12;
    const yVal = isMW ? -10 : -12;
    labels[i].g.element.lastElementChild.setAttribute('x', 0);
    labels[i].g.element.firstElementChild.setAttribute('x', xVal);
    let percentageCurrentX = Number(labels[i].g.element.children[1].getAttribute('x'));
    labels[i].g.element.children[1].setAttribute('x', percentageCurrentX + xVal);
    labels[i].g.element.lastElementChild.setAttribute('y', yVal);
  }

  return labels;
}

// function getLabelName(slice, options) {
//   if (options.optimizeDataOrder && Array.isArray(options.categories) &&
//       options.categories.length) {
//     return options.categories[0][slice.originalIndex] || slice.name;
//   }
//   return slice.name
// }


// Sorts labels intro quadrants.
function sortLabels(labels, rect) {
  let BR = [];
  let BL = [];
  let TL = [];
  let TR = [];

  labels = labels.sort(sortOnRadians);
  for (let i = 0; i < labels.length; i++) {
    let r = labels[i].radians;
    if (r > 0 && r <= Math.PI * 0.5) {
      BR.push(labels[i]);
    } else if (r > Math.PI * 0.5 && r < Math.PI) {
      BL.push(labels[i]);
    } else if (r >= Math.PI && r < Math.PI * 1.5) {
      TL.push(labels[i]);
    } else {
      TR.push(labels[i]);
    }
  }

  if (TR.length === 0 && BR.length) {
    TR.push(BR.shift());
  }
  BR.reverse();
  TL.reverse();
  return { BR, BL, TL, TR };
}


function positionLabels(highchart, sorted, rect) {
  // Top right
  let x;
  let y = 0;
  sorted.TR.forEach(label => {
    label.x = getRightX(highchart, label);
    label.y = y + label.lineHeight;
    transform.write(label.g.element, 'translate', label.x, label.y);
    y += label.lineHeight;
  });

  // Bottom right
  y = getBottomY(highchart);
  sorted.BR.forEach(label => {
    label.x = getRightX(highchart, label);
    label.y = y;
    transform.write(label.g.element, 'translate', label.x, label.y);
    y -= label.lineHeight;
  });

  // Bottom left
  x = getLeftX(highchart);
  y = getBottomY(highchart);
  sorted.BL.forEach(label => {
    label.x = x;
    label.y = y;
    transform.write(label.g.element, 'translate', label.x, label.y);
    y -= label.lineHeight;
  });

  // Top left
  y = 0;
  sorted.TL.forEach(label => {
    label.x = x;
    label.y = y + label.lineHeight;
    transform.write(label.g.element, 'translate', label.x, label.y);
    y += label.lineHeight;
  });
  return sorted;
}

// Returns default label left x.
function getLeftX(highchart) {
  return 0;//highchart.plotLeft;
}

// Returns default label right x.
function getRightX(highchart, label) {
  return highchart.plotLeft + highchart.plotWidth - highchart.spacing[1] -
    label.g.element.getBoundingClientRect().width;
}

// Returns default label bottom y.
function getBottomY(highchart) {
  return highchart.plotHeight - LABEL_MARGIN;// - highchart.spacing[2];
}


// Scales the chart down to compensate for labels.
function resizeChart(highchart, rect, sorted, lineHeight) {
  const maxLabelsTop = Math.max(sorted.TL.length, sorted.TR.length);
  const maxLabelsBot = Math.max(sorted.BL.length, sorted.BR.length);
  let newHeight = rect.height - (maxLabelsTop + maxLabelsBot) * lineHeight;
  newHeight = Math.max(newHeight, Math.min(highchart.plotWidth * 0.6,
    highchart.plotHeight * 0.6));

  let scaleRatio = newHeight / rect.height;
  let pieG = highchart.series[0].data[0].graphic.parentGroup;
  let pieContainer = pieG.element.parentElement;
  let translate = transform.read(pieG.element, 'translate');
  let x = (highchart.chartWidth - highchart.chartWidth * scaleRatio) * 0.5;
  let y = translate[1];
  y += maxLabelsTop * lineHeight;
  // y += (rect.height - newHeight) / 2;

  transform.write(pieG.element, 'translate', translate[0], 0);
  pieContainer.setAttribute('transform', [
    'matrix(',
    scaleRatio,
    ',0,0,',
    scaleRatio,
    ',',
    x,
    ',',
    y,
    ')'
  ].join(''));

  var r = {
    width: rect.width * scaleRatio
  };
  r.height = r.width;
  r.left = rect.center - r.width / 2 + highchart.spacing[3];
  r.center = rect.center;
  r.right = r.left + r.width;
  r.top = y + 10 * scaleRatio;
  r.middle = r.top + r.height / 2;
  r.bottom = r.top + r.height;
  // debugRect(highchart, r.left, r.top, r.width, r.height);
  return r;
}


// Truncate any labels that overlap the pie chart with '…'.
function truncateLabels(highchart, labels, rect) {
  const labelsLen = labels.length;
  if (labelsLen) {
    let parentGroup = labels[0].g.element.parentElement;
    let parentTop = transform.read(parentGroup, 'translate')[1];

    for (let i = 0; i < labelsLen; i++) {
      let label = labels[i];
      parentGroup = parentGroup || label.g.element.parentElement;
      var labelRect = label.g.element.getBoundingClientRect();
      label.bounds = {
        x: label.x + highchart.spacing[3],
        y: label.y + parentTop - parseFloat(label.styleLabel.fontSize) + highchart.spacing[0],
        width: labelRect.width,
        height: labelRect.height
      };
      // debugRect(highchart, testRect.x, testRect.y, testRect.width, testRect.height);

      var maxWidth;
      if (label.bounds.y > rect.top && label.bounds.y < rect.bottom) {
        // Label is next to pie chart.
        if (label.x < rect.left) {
          maxWidth = rect.left - label.x - LABEL_MARGIN;
          truncateLabelLeft(labelRect, label, rect.left - label.x - LABEL_MARGIN);
        } else {
          maxWidth = highchart.chartWidth - highchart.spacing[1] - rect.right;
          truncateLabelRight(labelRect, label,
            label.bounds.x + labelRect.width, maxWidth);
        }

      } else {
        // Label is above or below the pie.
        if (label.radians > Math.PI / 2 && label.radians < Math.PI * 1.5) {
          maxWidth = rect.center - label.x - LABEL_MARGIN;
          truncateLabelLeft(labelRect, label, maxWidth);
        } else {
          maxWidth = highchart.chartWidth - highchart.spacing[1] - rect.center;
          truncateLabelRight(labelRect, label,
            label.bounds.x + labelRect.width, maxWidth);
        }
      }
    }
  }
}

function truncateLabelLeft(labelRect, label, maxWidth) {
  let tspan = label.labelName.element.querySelector('tspan') ||
    label.labelName.element;
  let s = label.labelName.textStr;
  let n = s.length - 2;
  while (labelRect.width > maxWidth && n > 0) {
    s = s.substr(0, n--);
    tspan.innerHTML = s + '…';
    label.labelPerc.element.setAttribute('x',
      label.labelName.element.getBoundingClientRect().width + PERC_MARGIN);
    labelRect = label.g.element.getBoundingClientRect();
  }
}

function truncateLabelRight(labelRect, label, rightX, maxWidth) {
  let tspan = label.labelName.element.querySelector('tspan') ||
    label.labelName.element;
  let s = label.labelName.textStr;
  let n = s.length - 2;
  while (labelRect.width > maxWidth && n > 0) {
    s = s.substr(0, n--);
    tspan.innerHTML = s + '…';
    label.labelPerc.element.setAttribute('x',
      label.labelName.element.getBoundingClientRect().width + PERC_MARGIN);
    labelRect = label.g.element.getBoundingClientRect();
  }
  const translate = transform.read(label.g.element, 'translate');
  transform.write(label.g.element, 'translate', rightX - labelRect.width, translate[1]);
}


// Moves labels closer to the chart, if possible.
function improveLabelPositions(highchart, labels, rect) {
  let L = [];
  let R = [];
  let maxL = 0;
  let maxR = 0;
  for (var i = 0; i < labels.length; i++) {
    var label = labels[i];
    if (label.radians > Math.PI * 0.5 && label.radians < Math.PI * 1.5) {
      L.push(label);
      maxL = Math.max(maxL, label.bounds.width);
    } else {
      R.push(label);
      maxR = Math.max(maxR, label.bounds.width);
    }
  }
  var delta = rect.left - LABEL_MARGIN - maxL;
  if (delta > 0) {
    L.forEach(item => {
      let t = transform.read(item.g.element, 'translate');
      transform.write(item.g.element, 'translate', t[0] + delta, t[1]);
    })
  }
  delta = highchart.chartWidth - (rect.right + LABEL_MARGIN) - maxR;
  if (delta > 0) {
    R.forEach(item => {
      let t = transform.read(item.g.element, 'translate');
      transform.write(item.g.element, 'translate', t[0] - delta, t[1]);
    });
  }
}


// We decided, at least for the moment, not to do this.
function connectLabels(highchart, rect, labels) {
  //
}



// For sorting the labels from top to bottom.
function sortOnY(a, b) {
  // var ta = transform.read(a.dataLabel.element, 'translate')[1];
  // var tb = transform.read(b.dataLabel.element, 'translate')[1];
  // return ta - tb;
  return a.y - b.y;
}

// Sorts the labels clockwise around the pie, starting at 3 o'clock. The
// radians should already have been normalized to a number >= 0 and
// <= Math.PI * 2.
function sortOnRadians(a, b) {
  return a.radians - b.radians;
}

// Returns position and dimensions of the pie graphic.
function getPieRect(highchart) {
  const d = highchart.series[0].data[0];
  const g = !!d ? d.graphic : null;
  if (g) {
    let r = {
      left: g.x - g.r,
      top: g.y - g.r
    };
    r.width = g.r * 2;
    r.height = r.width;
    r.right = r.left + r.width;
    r.bottom = r.top + r.height;
    r.center = r.left + r.width / 2;
    r.middle = r.top  + r.height / 2;
    return r;
  }
  return null;
}


function debugRect(highchart, x, y, width, height) {
  return highchart.renderer.rect(x, y, width, height)
    .attr({
      'stroke-width': 1,
      'stroke': 'red',
      'fill': 'rgba(255, 255, 255, 0)'
    })
    .add();
}


// Used for determining whether to use 'small' or 'normal' font sizes.
function isSmall(size) {
  return size === 'D' || size === '4u' || size === 'fon';
}


function makeDonutChart(highchart, options) {
  if (__FINANCIALNEWS__) {
    const highchartContainer = highchart.container;
    const highchartContainerSVG = highchartContainer.firstElementChild;
    const pieRect = highchartContainerSVG.querySelectorAll('.highcharts-pie-series')[0];
    const rectRadius = pieRect.getBBox().width;
    const radius = rectRadius/4.1;
    const svgWidth = Number(highchartContainerSVG.getAttribute('width'));
    const svgHeight = highchartContainerSVG.getAttribute('height');
    const groupOfPieValue = highchartContainer.getElementsByClassName('highcharts-pie-series')[0];
    const lastPathOfPie = groupOfPieValue.lastElementChild.getAttribute('d').split(' ');
    const lastPathOfPieYVal = Number(lastPathOfPie[lastPathOfPie.length - 2]);
    const circle = highchart.renderer.circle(svgWidth / 2, lastPathOfPieYVal, radius).attr({
      fill: '#fefaf3'
    });
    const text = highchart.renderer.text('%', (svgWidth / 2) - (radius / 2) * .85 , lastPathOfPieYVal + (radius / 2) * .75).attr({
      'font-size': radius,
      stroke: 'black',
      text: '%'
    }).add()
    groupOfPieValue.append(circle.element);
    groupOfPieValue.append(text.element);
  }
}



export function renderInvalidated(rendered, options, invalidated) {
  if (__ALL__) {
    renderInvalidatedColors(rendered, invalidated);
  }
}



// WEBPACK FOOTER //
// ./src/chart/inner/chart-pie.js